import { DateTime } from "luxon";
import _ from "lodash";

export function isDateTimeMidnight(dateTime) {
    return (
        dateTime.hour === 0 &&
        dateTime.minute === 0 &&
        dateTime.second === 0 &&
        dateTime.millisecond === 0
    );
}

export function isoDateStringToIsoStringInSiteTimeZone(dateTimeISOString, siteTimeZone) {
    const dateTime = DateTime.fromISO(dateTimeISOString).setZone(siteTimeZone);
    return dateTimeObjToReadableDateTimeString(dateTime);
}

export function isoDateStringToIsoStringInLocalTimeZone(dateTimeISOString) {
    const dateTime = DateTime.fromISO(dateTimeISOString);
    return `${dateTime.toISODate()} ${dateTime.toLocaleString(DateTime.TIME_24_WITH_SHORT_OFFSET)}`;
}

export function dateTimeObjToReadableDateTimeString(dateTime) {
    return `${dateTime.toISODate()} ${dateTime.toLocaleString(DateTime.TIME_24_WITH_SHORT_OFFSET)}`;
}

export function dateTimeObjToReadableDateString(dateTime) {
    return `${dateTime.toISODate()}`;
}

export function dateTimeToDateTimeLocalFormFieldString(dateTime) {
    return dateTime.startOf("second").toISO({
        includeOffset: false,
        suppressMilliseconds: true,
    });
}

export function dateTimeToDateLocalFormFieldString(dateTime) {
    return dateTime.startOf("day").toISODate();
}

export function assertLuxonDateTime(dateTime) {
    if (!(dateTime instanceof DateTime)) {
        throw new Error("Expected a luxon DateTime object");
    }
}

export function toDateTimeForExperiment(isoDateTimeString, experiment) {
    return DateTime.fromISO(isoDateTimeString, { zone: experiment.siteTimeZone });
}

export function nowForExperiment(experiment) {
    return DateTime.now().setZone(experiment.siteTimeZone);
}

/**
 * The format is "yyyy-MM-dd, hh:mm AM"
 */
export function getDateTimeForTimestampDisplay(date) {
    if (!date) {
        return null;
    }

    const year = date.getFullYear();
    const month = (date.getMonth() + 1).toString().padStart(2, "0");
    const day = date.getDate().toString().padStart(2, "0");
    const hours = date.getHours();
    const minutes = date.getMinutes().toString().padStart(2, "0");
    const suffix = hours >= 12 ? "PM" : "AM";

    return `${year}-${month}-${day}, ${hours}:${minutes} ${suffix}`;
}

/**
 * returns the number of days between two dates (expects Luxon DateTime objects!)
 */
export function getDaysBetweenDates(date1, date2) {
    const startOfDay1 = date1.startOf("day");
    const startOfDay2 = date2.startOf("day");
    return Math.floor(startOfDay2.diff(startOfDay1, "days").as("days"));
}

export function buildDayByDateForExperimentStartDateField(
    selectedDay,
    experimentStartDate,
    changedCallback
) {
    const date = experimentStartDate.plus({ days: selectedDay !== null ? selectedDay : 0 });

    return {
        fieldType: "date",
        fieldDisplayName: "Date",
        value: selectedDay !== null ? date.toISODate() : null,
        changed: (value) => {
            // value is a string "YYYY-MM-DD"
            const selectedDateTime = DateTime.fromISO(value);
            const days = getDaysBetweenDates(experimentStartDate, selectedDateTime);
            changedCallback(days, selectedDateTime, experimentStartDate);
        },
    };
}

export function getDateTimeForISODateOnlyString(date) {
    if (_.isNil(date)) {
        return null;
    }

    return DateTime.fromISO(date, { zone: "UTC" });
}

export function getDateTimeStringForDisplayInLocalTimeZone(date) {
    if (_.isNil(date)) {
        return null;
    }

    return DateTime.fromISO(date, { zone: "UTC" })
        .setZone("local")
        .toLocaleString(DateTime.DATETIME_MED_WITH_SECONDS);
}

export function getISODateOnlyStringInSiteTimeZone(date, siteTimeZone) {
    if (_.isNil(date)) {
        return null;
    }

    return DateTime.fromISO(date, { zone: "UTC" }).toISODate({ zone: siteTimeZone });
}

export function getISODateOnlyStringInLocalTimeZone(date) {
    if (_.isNil(date)) {
        return null;
    }

    return DateTime.fromISO(date, { zone: "UTC" }).setZone("local").toISODate();
}

export function getCurrentDateOnlyValue() {
    // Note: UTC is used here because the value stored is a date only, not a date-time.
    // This keeps the date the same regardless of timezone referenced.
    return DateTime.local({ zone: "UTC", hour: 0, minute: 0, second: 0, millisecond: 0 });
}

export function getDateOnlyValue(date) {
    return DateTime.fromISO(date, { zone: "UTC" }).toISODate();
}

export function validateDateString(dateString) {
    const date = new Date(dateString);
    if (isNaN(date.getTime())) {
        throw new Error(`Expected a date but got: ${dateString}`);
    }
}
